import {
  RecurringFileList,
  RecurringRequestListPagination,
  ValidationRecurringBody,
} from '@back-office/http';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Typography,
} from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Pagination from '@material-ui/lab/Pagination';
import { lowerCase } from 'lodash';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyIcon } from '../../icons';
import { getDatetimeText } from '../../utils';
import ApproveRecurringFileDialog from '../approve-recurring-file-dialog';
import GcpBox from '../gcp-box';
import GcpCurrencyFormat from '../gcp-currency-format';
import GcpRecurringStatus, { RecurringStatus } from '../gcp-recurring-status';
import GcpTypography from '../gcp-typography';
import RejectRecurringFileDialog from '../reject-recurring-file-dialog';
import tableHeader from './content';
import useStyles from './styles';
import useViewModel from './viewmodel';

interface GcpUploadLogTableProps {
  data: RecurringFileList[];
  pagination: RecurringRequestListPagination;
  page: number;
  count: number;
  multiselect: boolean;
  isCheckerPermission?: string;
  isAdmin: boolean;
  handleOnChangePage: (newPage: number) => void;
  handleApproveRecurringFile: (id: string[], requestedAt: string) => void;
  handleRejectRecurringFile: (id: string[], reason: string) => void;
  handleDownloadFile: (fileName: string) => void;
  handleCheckListRecurringFile: (data: {
    onboardBatchID: string[];
  }) => Promise<ValidationRecurringBody>;
}

interface FileTypeProps {
  file_name: string;
  id: string;
}

export interface ApproveRecurringListProps {
  showTab: boolean;
  allpass: boolean;
  approveLists: FileTypeProps[];
  failedLists: FileTypeProps[];
}

export interface BatchProps {
  onboardBatchID: string;
  result: boolean;
}

const GcpUploadLogTable: FC<GcpUploadLogTableProps> = observer((props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    data,
    multiselect,
    pagination,
    page,
    count,
    isCheckerPermission,
    isAdmin,
    handleOnChangePage,
    handleApproveRecurringFile,
    handleRejectRecurringFile,
    handleDownloadFile,
    handleCheckListRecurringFile,
  } = props;
  const {
    selected,
    handleSelectAllClick,
    handleClick,
    isSelected,
    statusConverter,
    findBatchNameFormId,
    approveDialog,
    toggleApproveDialog,
    rejectDialog,
    toggleRejectDialog,
    approveRecurringLists,
    setSelected,
    getFileStatusInfo,
    isShowButton,
    approveDialogOpenTime,
  } = useViewModel({
    data,
    multiselect,
    handleCheckListRecurringFile,
    isCheckerPermission,
    isAdmin,
  });

  return (
    <>
      <TableContainer component="div" style={{ maxWidth: 1200 }}>
        <Table aria-label="GCP Upload Logs" className={classes.table}>
          <TableHead>
            <TableRow>
              {tableHeader.map(
                (cell, index) =>
                  (!cell.action || isCheckerPermission || isAdmin) && (
                    <TableCell
                      key={t(cell.title)}
                      align="left"
                      className={`${classes.headerCell} ${
                        cell.action &&
                        (isCheckerPermission || isAdmin) &&
                        'last-child'
                      }`}
                    >
                      {index === 0 &&
                      multiselect &&
                      (isCheckerPermission || isAdmin) ? (
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item style={{ paddingLeft: '16px' }} xs={4}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    data.length > 0 &&
                                    selected.length === data.length
                                  }
                                  color="primary"
                                  indeterminate={
                                    selected.length > 0 &&
                                    selected.length < data.length
                                  }
                                  inputProps={{
                                    'aria-label': 'select all items',
                                  }}
                                  onChange={handleSelectAllClick}
                                />
                              }
                              label={t('payment_file.upload_logs.header.all')}
                            />
                          </Grid>
                          <Grid item alignItems="center" xs={8}>
                            <GcpTypography
                              paint="mineShaft"
                              variant="body1"
                              whiteSpace="pre-line"
                            >
                              {t(cell.title)}
                            </GcpTypography>
                          </Grid>
                        </Grid>
                      ) : (
                        <GcpBox ml={2}>
                          <GcpTypography
                            paint="mineShaft"
                            variant="body1"
                            whiteSpace="pre-line"
                          >
                            {cell.title.startsWith('!') ? '' : t(cell.title)}
                          </GcpTypography>
                        </GcpBox>
                      )}
                    </TableCell>
                  )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {data?.map((item, index) => {
              const isItemSelected = isSelected(item.id);

              return (
                <TableRow
                  key={item.id}
                  hover
                  aria-checked={isItemSelected}
                  className={classes.bodyRow}
                  selected={isItemSelected}
                  tabIndex={-1}
                >
                  <TableCell className={classes.bodyCell}>
                    {multiselect ? (
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="flex-start"
                        spacing={1}
                      >
                        <Grid item style={{ paddingLeft: 0 }} xs={4}>
                          <Checkbox
                            checked={isItemSelected}
                            color="primary"
                            style={{ paddingLeft: 0 }}
                            onClick={() => handleClick(item.id)}
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <GcpBox alignItems="center" display="flex" gap={1}>
                            <GcpRecurringStatus
                              size={8}
                              status={
                                lowerCase(item.status || '') as RecurringStatus
                              }
                            />
                            <GcpTypography paint="mineShaft" variant="body1">
                              {t(
                                `payment_file.upload_logs.status.${
                                  statusConverter?.[lowerCase(item.status)]
                                }`,
                                {
                                  reason: item?.reject_reason
                                    ? `- ${item.reject_reason}`
                                    : '',
                                }
                              )}
                            </GcpTypography>
                          </GcpBox>
                        </Grid>
                      </Grid>
                    ) : (
                      <GcpBox alignItems="center" display="flex" gap={1}>
                        <GcpRecurringStatus
                          size={8}
                          status={
                            lowerCase(item.status || '') as RecurringStatus
                          }
                        />
                        <GcpTypography
                          className={classes.status}
                          paint="mineShaft"
                          variant="body1"
                        >
                          {t(
                            `payment_file.upload_logs.status.${
                              statusConverter?.[lowerCase(item.status)]
                            }`,
                            {
                              reason: item?.reject_reason
                                ? `- ${item.reject_reason}`
                                : '',
                            }
                          )}
                        </GcpTypography>
                      </GcpBox>
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.batch_name}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.scheduled_at
                        ? getDatetimeText(item.scheduled_at, 'scheduleDate')
                        : ''}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpCurrencyFormat
                      fixedDecimalScale
                      paint="mineShaft"
                      value={item.total_amount_string}
                      valueVariant="body1"
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpCurrencyFormat
                      paint="mineShaft"
                      value={item.total_record}
                      valueVariant="body1"
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.created_by}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <Link
                      style={{ fontWeight: 400 }}
                      target="_blank"
                      onClick={() => handleDownloadFile(item?.batch_name)}
                    >
                      original.xlsx
                    </Link>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {item.updated_by}
                    </GcpTypography>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    <GcpTypography paint="mineShaft" variant="body1">
                      {getDatetimeText(item.updated_at, 'default')}
                    </GcpTypography>
                  </TableCell>

                  {(isCheckerPermission || isAdmin) && (
                    <TableCell className={`${classes.bodyCell} last-child`}>
                      {multiselect
                        ? index === 0 && (
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography
                                  className={
                                    selected.length === 0 && classes.disable
                                  }
                                  color={
                                    selected.length !== 0
                                      ? 'primary'
                                      : 'textSecondary'
                                  }
                                  onClick={() => {
                                    if (selected.length !== 0) {
                                      getFileStatusInfo('multiple');
                                    }
                                  }}
                                >
                                  Approve selected
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  className={
                                    selected.length === 0 && classes.disable
                                  }
                                  color={
                                    selected.length !== 0
                                      ? 'primary'
                                      : 'textSecondary'
                                  }
                                  onClick={() => {
                                    if (selected.length !== 0) {
                                      toggleRejectDialog(true);
                                    }
                                  }}
                                >
                                  Reject selected
                                </Typography>
                              </Grid>
                            </Grid>
                          )
                        : isShowButton(item) && (
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography
                                  color="primary"
                                  onClick={() => {
                                    setSelected([item.id]);
                                    getFileStatusInfo('single', [item.id]);
                                  }}
                                >
                                  Approve
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  color="primary"
                                  onClick={() => {
                                    setSelected([item.id]);
                                    toggleRejectDialog(true);
                                  }}
                                >
                                  Reject
                                </Typography>
                              </Grid>
                            </Grid>
                          )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ApproveRecurringFileDialog
          allpass={approveRecurringLists?.allpass}
          approvedLists={approveRecurringLists?.approveLists}
          failedLists={approveRecurringLists?.failedLists}
          open={approveDialog}
          showTab={approveRecurringLists?.showTab}
          onCancel={() => toggleApproveDialog(false)}
          onSubmit={() => {
            handleApproveRecurringFile(selected, approveDialogOpenTime);
            toggleApproveDialog(false);
          }}
        />
        <RejectRecurringFileDialog
          batchName={findBatchNameFormId(selected)}
          id={selected}
          open={rejectDialog}
          onCancel={() => toggleRejectDialog(false)}
          onSubmit={(reason: string | undefined) => {
            handleRejectRecurringFile(selected, reason);
            toggleRejectDialog(false);
          }}
        />
      </TableContainer>
      {data.length === 0 && (
        <GcpBox
          isFullWidth
          alignItems="center"
          className={classes.empty}
          display="flex"
          flexDirection="column"
          mt={8}
        >
          <EmptyIcon className={classes.empty} />
          <GcpTypography
            paint="hitGray"
            textAlign="center"
            variant="body1"
            whiteSpace="pre-line"
          >
            {t('payment_file.no_data')}
          </GcpTypography>
        </GcpBox>
      )}
      {data.length !== 0 && (
        <GcpBox display="flex" justifyContent="flex-end" mb={4} mt={4}>
          <GcpBox alignItems="center" display="flex" mr={2.5}>
            <GcpTypography variant="body1">
              {t('biller_enrollment_list.viewing', {
                start: (pagination.current_page - 1) * count + 1,
                end: Math.min(
                  pagination.current_page * count,
                  pagination.total_count
                ),

                total: pagination.total_count,
              })}
            </GcpTypography>
          </GcpBox>
          <Pagination
            color="primary"
            count={Math.ceil(pagination.total_count / count)}
            page={page}
            shape="rounded"
            variant="outlined"
            onChange={(_event, pageNumber) => {
              setSelected([]);
              handleOnChangePage(pageNumber);
            }}
          />
        </GcpBox>
      )}
    </>
  );
});

export default GcpUploadLogTable;
